import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { type FunctionComponent } from "react";

interface PaneContextMenuAnchor {
  // Viewport coordinates of the right click
  top: number;
  left: number;
}

interface PaneContextMenuProps {
  anchor: PaneContextMenuAnchor | null;
  // Both are false when there's nothing left to collapse or expand, which also
  // covers the empty canvas
  canCollapseAll: boolean;
  canExpandAll: boolean;
  canLayout: boolean;
  onClose: () => void;
  onCollapseAll: () => void;
  onExpandAll: () => void;
  onAutoLayout: () => void;
}

/**
 * The right-click menu of the canvas itself.
 *
 * It holds the actions that apply to the graph as a whole, as opposed to
 * {@link NodeContextMenu} which acts on the one node under the cursor.
 */
const PaneContextMenu: FunctionComponent<PaneContextMenuProps> = ({
  anchor,
  canCollapseAll,
  canExpandAll,
  canLayout,
  onClose,
  onCollapseAll,
  onExpandAll,
  onAutoLayout,
}) => {
  // Runs the action and closes in one go, which is what every item wants
  const run = (action: () => void) => {
    return () => {
      action();
      onClose();
    };
  };

  return (
    <Menu
      open={anchor !== null}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={
        anchor === null ? undefined : { top: anchor.top, left: anchor.left }
      }
      // React Flow opens this menu on mouse up, one event before the browser
      // fires `contextmenu`. By then the modal backdrop covers the canvas, so
      // React Flow's own handler no longer recognizes the event as a pane click
      // and stops suppressing the native menu. Swallowing it here keeps
      // Chrome's menu from opening on top of this one
      onContextMenu={(event) => {
        event.preventDefault();
      }}
      // Right clicking elsewhere while the menu is open should feel instant
      // rather than animated
      transitionDuration={80}
      MenuListProps={{ dense: true, "aria-label": "pane-context-menu" }}
      slotProps={{ paper: { sx: { minWidth: 208 } } }}
    >
      <MenuItem disabled={!canCollapseAll} onClick={run(onCollapseAll)}>
        <ListItemIcon>
          <UnfoldLessIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Collapse all nodes</ListItemText>
      </MenuItem>

      <MenuItem disabled={!canExpandAll} onClick={run(onExpandAll)}>
        <ListItemIcon>
          <UnfoldMoreIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Expand all nodes</ListItemText>
      </MenuItem>

      <Divider />

      <MenuItem disabled={!canLayout} onClick={run(onAutoLayout)}>
        <ListItemIcon>
          <AccountTreeOutlinedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Auto layout</ListItemText>
      </MenuItem>
    </Menu>
  );
};

export default PaneContextMenu;
export type { PaneContextMenuAnchor };
